import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { OpinlyContent } from "@opinly/react";
import { calculateReadingTime, imageUrl } from "@opinly/shared";
import { usePageMeta } from "../../lib/usePageMeta";
import { formatPostDate, opinlyConfig, postSeo } from "../../lib/opinly";
import { PostFaqs } from "./PostFaqs";

export const BlogArticle = ({ post, imagesPrefix }) => {
  const config = useMemo(() => opinlyConfig(imagesPrefix), [imagesPrefix]);
  usePageMeta(postSeo(post, config));

  // Images render only once the Opinly CDN address is configured on the server.
  const components = useMemo(() => ({
    image: ({ node }) => (config.imagesPrefix && node.attrs?.fileKey ? (
      <img src={imageUrl(node.attrs.fileKey, config)} alt={node.attrs?.alt ?? ""} title={node.attrs?.title ?? undefined} loading="lazy" decoding="async" />
    ) : null),
  }), [config]);

  const hero = config.imagesPrefix && post.titleFile?.fileKey ? imageUrl(post.titleFile.fileKey, config) : null;
  const minutes = post.content ? calculateReadingTime(post.content) : null;

  return (
    <article className="bg-white" data-testid="blog-post-page">
      <header className="mps-container max-w-3xl pt-12 md:pt-16">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-brand-muted hover:text-brand-navy transition-colors">
          <ArrowLeft size={14} weight="bold" /> All articles
        </Link>
        {post.category?.name && <div className="eyebrow mt-8 mb-4">{post.category.name}</div>}
        <h1 data-testid="blog-post-title" className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-brand-navy leading-[1.1]">
          {post.title}
        </h1>
        {post.description && <p className="mt-5 text-base md:text-lg text-brand-muted leading-relaxed">{post.description}</p>}
        <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1 text-sm text-brand-muted" data-testid="blog-post-meta">
          {post.author?.name && <span className="font-medium text-brand-navy">{post.author.name}</span>}
          <time dateTime={post.firstPublishedAt}>{formatPostDate(post.firstPublishedAt)}</time>
          {minutes && <span>{minutes} min read</span>}
        </div>
      </header>

      {hero && (
        <figure className="mps-container max-w-4xl mt-10">
          <img src={hero} alt={post.titleFile?.altText || post.title} data-testid="blog-post-hero" className="w-full aspect-[16/9] object-cover rounded-[16px] bg-brand-surface" />
          {post.titleFile?.caption && <figcaption className="mt-3 text-xs text-brand-muted">{post.titleFile.caption}</figcaption>}
        </figure>
      )}

      <div className="mps-container max-w-3xl py-10 md:py-14">
        <div className="mps-prose" data-testid="blog-post-content">
          {post.content && <OpinlyContent content={post.content} config={config} components={components} />}
        </div>
        <PostFaqs faqs={post.faqs} />
        <aside className="mt-14 rounded-[16px] bg-brand-surface border border-brand-border p-6 md:p-8" data-testid="blog-post-ruo-note">
          <p className="text-sm text-brand-muted leading-relaxed">
            Informational content for laboratory researchers. Products referenced are research reference standards —
            for research use only, not for human or veterinary use.
          </p>
          <Link to="/shop" data-testid="blog-post-shop-cta" className="inline-flex mt-5 items-center gap-2 h-11 px-5 rounded-[12px] bg-brand-navy hover:bg-brand-navy2 text-white text-sm font-semibold transition-colors">
            Browse the catalog <ArrowRight size={14} weight="bold" />
          </Link>
        </aside>
      </div>
    </article>
  );
};
